import retry from 'fetch-retry';
import { map, trim } from 'lodash';
import download from './download';
import {
  AuthorizationError,
  BadRequestError,
  NetworkError,
  NotFoundError,
  OfflineError,
  RequestError,
  ServiceUnavailableError,
  UpdateRequiredError
} from './errors';

type Options = {
  baseUrl?: string;
  ['accept-language']?: string;
};

const fetchWithRetry = retry(fetch);

class ApiClient {
  baseUrl: string;

  userAgent: string;

  acceptLanguage = 'en';

  constructor(options: Options = {}) {
    this.baseUrl = options.baseUrl || '/api';
    this.userAgent = 'OutlineFrontend';

    if (options['accept-language'] === 'ur') {
      this.acceptLanguage = 'ur';
    } else if (options['accept-language'] === 'all') {
      this.acceptLanguage = 'all';
    } else if (options['accept-language'] === 'en') {
      this.acceptLanguage = 'en';
    }
    // alert(this.acceptLanguage)
  }

  fetch = async (
    path: string,
    method: string,
    data: Record<string, any> | FormData | undefined,
    options: Record<string, any> = {}
  ) => {
    let body: string | FormData | undefined;
    let modifiedPath;
    let isJson;

    if (method === 'GET') {
      if (data) {
        modifiedPath = `${path}?${data && this.constructQueryString(data)}`;
      } else {
        modifiedPath = path;
      }
    } else if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
      if (data instanceof FormData || typeof data === 'string') {
        body = data;
      }

      // Only stringify data if its a normal object and
      // not if it's [object FormData], in addition to
      // toggling Content-Type to application/json
      if (
        typeof data === 'object' &&
        (data || '').toString() === '[object Object]'
      ) {
        isJson = true;
        body = JSON.stringify(data);
      }
    }

    let baseUrl = this.baseUrl;

    if (
      path === '/users/login' ||
      path === 'users/login' ||
      path === '/users/reset-password' ||
      path === '/users/verify-reset-password'
    ) {
      baseUrl = process.env.REACT_APP_LOGIN_URL || '';
    }

    const urlToFetch = path.match(/^http/)
      ? path
      : baseUrl + (modifiedPath || path);

    const headerOptions: any = {
      Accept: 'application/json',
      'cache-control': 'no-cache',
      pragma: 'no-cache',
      'accept-language': 'en'
      // 'accept-language': this.acceptLanguage,
    };

    // for multipart forms or other non JSON requests fetch
    // populates the Content-Type without needing to explicitly
    // set it.
    if (isJson) {
      headerOptions['Content-Type'] = 'application/json';
    }
    // headerOptions['accept-language'] = this.acceptLanguage;

    // Allow full override (including accept-language)
    if (options?.headers) {
      Object.assign(headerOptions, options.headers);
    }

    const headers = new Headers(headerOptions);

    /**when is authenticated send token along in the header */
    const getAccessToken = localStorage.getItem('access-token');
    const accessToken = getAccessToken ? JSON.parse(getAccessToken) : null;
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

    let response;

    try {
      // alert(urlToFetch+""+JSON.stringify(headers))
      response = await fetchWithRetry(urlToFetch, {
        method,
        body,
        headers,
        cache: 'no-cache'
      });
    } catch (err) {
      console.error('Error : ', err);
      if (window.navigator.onLine) {
        throw new NetworkError('A network error occurred, try again?');
      } else {
        throw new OfflineError('No internet connection available');
      }
    }

    const success = response.status >= 200 && response.status < 300;

    if (options.download && success) {
      const blob = await response.blob();
      const fileName = (
        response.headers.get('content-disposition') || ''
      ).split('filename=')[1];
      download(blob, trim(fileName, '"'));
      return;
    } else if (success && response.status === 204) {
      return;
    } else if (success) {
      return response.json();
    }

    // Handle 401, log out user
    // if (response.status === 401) {
    //   do logout
    //   return;
    // }

    // Handle failed responses
    const error: {
      statusCode?: number;
      response?: Response;
      message?: string;
      error?: string;
      data?: Record<string, any>;
    } = {};

    error.statusCode = response.status;
    error.response = response;

    try {
      const parsed = await response.json();
      error.message = parsed.message || '';
      error.error = parsed.error;
      error.data = parsed.data;
    } catch (_err) {
      // we're trying to parse an error so JSON may not be valid
    }

    if (response.status === 401 && error.message === 'jwt expired') {
      throw new BadRequestError(error.message);
    }

    if (response.status === 400 && error.error === 'editor_update_required') {
      window.location.reload();
      throw new UpdateRequiredError(error.message);
    }

    if (response.status === 400) {
      throw new BadRequestError(error.message);
    }

    if (response.status === 403) {
      throw new AuthorizationError(error.message);
    }

    if (response.status === 404) {
      throw new NotFoundError(error.message);
    }

    if (response.status === 503) {
      throw new ServiceUnavailableError(error.message);
    }

    throw new RequestError(error.message);
  };

  get = async (
    path: string,
    data?: Record<string, any> | undefined,
    options?: Record<string, any>
  ) => {
    const res = await this.fetch(path, 'GET', data, options);
    console.log('Res', res);
    return res;
  };

  post = (
    path: string,
    data: Record<string, any> | undefined,
    options?: Record<string, any>
  ) => {
    return this.fetch(path, 'POST', data, options);
  };

  put = (
    path: string,
    data: Record<string, any> | undefined,
    options?: Record<string, any>
  ) => {
    return this.fetch(path, 'PUT', data, options);
  };
  delete = (
    path: string,
    data: Record<string, any> | undefined,
    options?: Record<string, any>
  ) => {
    return this.fetch(path, 'DELETE', data, options);
  };

  // Helpers
  constructQueryString = (data: Record<string, any>) => {
    return map(
      data,
      (v, k) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`
    ).join('&');
  };
}

export default ApiClient; // In case you don't want to always initiate, just import with `import { client } ...`

export const client = new ApiClient({ baseUrl: process.env.REACT_APP_API_URL });
